import { Button } from "primereact/button";
import React, { useState } from "react";
import styled from "styled-components";

import { IClaim } from "../../types/claim";
import { BulkEditPayerModal } from "./BulkEditPayerModal";
import { BulkEditStatusModal } from "./BulkEditStatusModal";
import { BulkReprocessModal } from "./BulkReprocessModal";
import { BulkRunAiAnalysisModal } from "./BulkRunAiAnalysisModal";

interface IBulkClaimActionsBarProps {
  selectedClaims: IClaim[];
  maxBulkSize: number;
  onClearSelection: () => void;
  accountId: string;
  /** Called after a bulk update succeeds — typically clears selection in the parent. */
  onActionSuccess: () => void;
}

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 6px;
  flex-wrap: wrap;
`;

const Count = styled.span`
  font-weight: 600;
  color: #1565c0;
`;

const ClearLink = styled.button`
  background: none;
  border: none;
  color: #1565c0;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: 0.875rem;
  &:hover {
    color: #0d47a1;
  }
`;

const CapWarning = styled.span`
  color: #d84315;
  font-size: 0.875rem;
`;

const Spacer = styled.div`
  flex: 1;
`;

/**
 * CLE-2172: bulk action bar that appears above the claims list when one or more
 * rows are selected. Four actions: update matched payer, update resolution
 * status, reprocess, and run AI analysis (CLE-2551). Each opens its own confirm
 * modal. Selection is single-page
 * only (the parent clears it on page/filter changes) so this bar is page-scoped.
 */
export function BulkClaimActionsBar({
  selectedClaims,
  maxBulkSize,
  onClearSelection,
  accountId,
  onActionSuccess,
}: IBulkClaimActionsBarProps) {
  const [payerModalOpen, setPayerModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [reprocessModalOpen, setReprocessModalOpen] = useState(false);
  const [aiAnalysisModalOpen, setAiAnalysisModalOpen] = useState(false);

  const atCap = selectedClaims.length >= maxBulkSize;

  return (
    <Bar>
      <Count>
        {selectedClaims.length} claim{selectedClaims.length === 1 ? "" : "s"}{" "}
        selected
      </Count>
      <ClearLink type="button" onClick={onClearSelection}>
        Clear selection
      </ClearLink>
      {atCap && (
        <CapWarning>
          {maxBulkSize} / {maxBulkSize} max — apply this batch, then continue on
          the next page.
        </CapWarning>
      )}
      <Spacer />
      <Button
        label="Update Matched Payer"
        icon="pi pi-pencil"
        className="p-button-sm"
        onClick={() => setPayerModalOpen(true)}
      />
      <Button
        label="Update Status"
        icon="pi pi-flag"
        className="p-button-sm p-button-outlined"
        onClick={() => setStatusModalOpen(true)}
      />
      <Button
        label="Reprocess"
        icon="pi pi-refresh"
        className="p-button-sm p-button-outlined"
        onClick={() => setReprocessModalOpen(true)}
      />
      <Button
        label="Run AI Analysis"
        icon="pi pi-sparkles"
        className="p-button-sm p-button-outlined"
        onClick={() => setAiAnalysisModalOpen(true)}
      />

      <BulkEditPayerModal
        visible={payerModalOpen}
        onHide={() => setPayerModalOpen(false)}
        selectedClaims={selectedClaims}
        accountId={accountId}
        onSuccess={() => {
          setPayerModalOpen(false);
          onActionSuccess();
        }}
      />

      <BulkEditStatusModal
        visible={statusModalOpen}
        onHide={() => setStatusModalOpen(false)}
        selectedClaims={selectedClaims}
        onSuccess={() => {
          setStatusModalOpen(false);
          onActionSuccess();
        }}
      />

      <BulkReprocessModal
        visible={reprocessModalOpen}
        onHide={() => setReprocessModalOpen(false)}
        selectedClaims={selectedClaims}
        onSuccess={() => {
          setReprocessModalOpen(false);
          onActionSuccess();
        }}
      />

      <BulkRunAiAnalysisModal
        visible={aiAnalysisModalOpen}
        onHide={() => setAiAnalysisModalOpen(false)}
        selectedClaims={selectedClaims}
        onSuccess={() => {
          setAiAnalysisModalOpen(false);
          onActionSuccess();
        }}
      />
    </Bar>
  );
}
